import { useAuth } from "../../context/AuthContext";
import { useMemo, useState } from "react";
import { useToast } from "../../components/ui/toast";
import { useCallbacks } from "../../hooks/use-callbacks";
import { formatCallbackWhen } from "../../lib/time";
import { CallbacksToolbar } from "./callbacks-toolbar";
import { CallbacksList } from "./callbacks-list";
import { RescheduleModal } from "./reschedule-modal";
import { DateRangePicker, presetRange, type DateRangeValue } from "../../components/ui/date-range-picker";
import type { Callback } from "./types";
import type { FilterState } from "../../components/ui/filter-popover";

export function CallbacksPage() {
  const { user } = useAuth() as any;
  const globalTz = user?.timezone;
  const [dateRange, setDateRange] = useState<DateRangeValue>(() => ({
    ...presetRange("last_7"),
    preset: "last_7",
  }));
  const {
    callbacks,
    updateStatus,
    deleteCallback,
    rescheduleCallback,
    refresh,
    loading,
    hasMore,
    loadMore,
  } = useCallbacks(dateRange);
  const toast = useToast();

  const [filters, setFilters] = useState<FilterState>({
    classification: "all",
    status: "all"
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<Callback | null>(null);

  const filtered = useMemo(() => {
    const q = (filters.search || "").trim().toLowerCase();
    return callbacks.filter((cb) => {
      if (q && ![cb.scheduleId, cb.contactName, cb.phone, cb.contactId]
        .some((v) => v.toLowerCase().includes(q))) return false;
      if (filters.classification && filters.classification !== "all" && cb.classification !== filters.classification) return false;
      if (filters.status && filters.status !== "all" && cb.status !== filters.status) return false;
      return true;
    });
  }, [callbacks, filters]);

  const isFiltered: boolean = Boolean(filters.search) || filters.classification !== "all" || filters.status !== "all";
  const clearFilters = () => setFilters({ classification: "all", status: "all" });

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const pauseResume = async (id: string) => {
    const cb = callbacks.find((c) => c.id === id);
    if (!cb) return;
    const pausing = cb.status !== "paused";
    try {
      await updateStatus([id], pausing ? "paused" : "scheduled");
      pausing
        ? toast.warning("Callback paused", `${cb.contactName} - ${cb.scheduleId}`)
        : toast.success("Callback resumed", `${cb.contactName} - ${cb.scheduleId}`);
    } catch (error) {
      toast.danger("Callback update failed", (error as Error).message);
    }
  };

  const bulk = async (next: "paused" | "scheduled") => {
    const n = selectedIds.size;
    try {
      await updateStatus([...selectedIds], next);
      setSelectedIds(new Set());
      next === "paused"
        ? toast.warning(`${n} callback${n === 1 ? "" : "s"} paused`)
        : toast.success(`${n} callback${n === 1 ? "" : "s"} resumed`);
    } catch (error) {
      toast.danger("Bulk callback update failed", (error as Error).message);
    }
  };

  const remove = async (id: string) => {
    const cb = callbacks.find((c) => c.id === id);
    if (!cb) return;
    if (window.confirm(`Delete callback for ${cb.contactName}? This cannot be undone.`)) {
      try {
        await deleteCallback(id);
        setSelectedIds((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
        toast.danger("Callback deleted", `${cb.contactName} - ${cb.scheduleId}`);
      } catch (error) {
        toast.danger("Callback deletion failed", (error as Error).message);
      }
    }
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col space-y-4 px-4 sm:px-5 md:px-6 pb-6 pt-3 sm:pt-4">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-sm font-semibold text-ink">Callbacks</h1>
          
        </div>
      </div>

      <CallbacksToolbar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        filters={filters}
        onFilterChange={setFilters}
        selectedCount={selectedIds.size}
        onBulkPause={() => bulk("paused")}
        onBulkResume={() => bulk("scheduled")}
        onClearFilters={clearFilters}
        onRefresh={() => {
          Promise.resolve(refresh())
            .then(() => toast.success("Callbacks refreshed"))
            .catch((error: unknown) => toast.danger("Refresh failed", (error as Error).message));
        }}
        isFiltered={isFiltered}
      />

      <CallbacksList
        callbacks={filtered}
        expandedId={expandedId}
        selectedIds={selectedIds}
        isFiltered={isFiltered}
        hasMore={hasMore}
        loading={loading}
        onLoadMore={loadMore}
        onClearFilters={clearFilters}
        onToggleExpand={(id) => setExpandedId((cur) => (cur === id ? null : id))}
        onToggleSelect={toggleSelect}
        onPauseResume={pauseResume}
        onReschedule={(id) => setRescheduleTarget(callbacks.find((c) => c.id === id) ?? null)}
      />

      {rescheduleTarget && (
        <RescheduleModal
          callback={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onConfirm={async (date, time, timezone) => {
            try {
              await rescheduleCallback(rescheduleTarget.id, date, time, timezone);
              toast.success(
                "Callback rescheduled",
                `${rescheduleTarget.contactName} - ${formatCallbackWhen(date, time, timezone, globalTz)} (${timezone})`,
              );
              setRescheduleTarget(null);
            } catch (error) {
              toast.danger("Reschedule failed", (error as Error).message);
            }
          }}
        />
      )}
    </div>
  );
}
